import { useEffect } from 'react';
import type { TravelMapComponentFragment } from '../../../features/custom-page/WebsiteEditorCustomPage.graphql.generated';
import { getAccommodationsNavigations } from '../Accommodations.navigations';
import { ACCOMMODATIONS_ACTIONS, ACCOMMODATIONS_ANIMATION_CONFIG, ACCOMMODATIONS_SCREENS } from '../Accommodations.constants';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useQueryParamHelper } from '@shared/core/queryString';
import { CUSTOM_PROPERTY_ADD_KEY } from '../Accommodations.constants';
import type { CustomComponentsProps } from '@website-editor/customComponents/CustomComponents';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';

type AccommodationsSetupArgs = Readonly<
  CustomComponentsProps & {
    componentData: TravelMapComponentFragment;
  }
>;

/**
 * Shared setup logic for both V1 and V2 Accommodations:
 * - Syncs block ordering into CustomComponentState
 * - Registers navigation pane screens and animations
 * - Handles deep-link to the "add custom property" screen
 */
export const useAccommodationsSetup = ({ componentData, ...props }: AccommodationsSetupArgs) => {
  const { screens, addNavigations, navigateToScreen } = useNavigationPaneContext();
  const queryParamHelper = useQueryParamHelper();
  const isAddCustomPropertyOpen = queryParamHelper.getValueString(CUSTOM_PROPERTY_ADD_KEY);

  const { setBlockOrderData } = useCustomComponentStateContext();
  useEffect(() => {
    if (componentData) {
      setBlockOrderData({ id: componentData.id, sortOrder: componentData.sortOrder, blockOrdering: componentData.blockOrdering ?? [] });
    }
  }, [componentData, setBlockOrderData]);

  const accommodationsNavigations = getAccommodationsNavigations({ ...props, componentId: componentData.id });

  useEffect(() => {
    if (!screens.includes(ACCOMMODATIONS_SCREENS[0])) {
      addNavigations(ACCOMMODATIONS_SCREENS, ACCOMMODATIONS_ANIMATION_CONFIG, accommodationsNavigations);
    }
  }, [addNavigations, screens, accommodationsNavigations]);

  useEffect(() => {
    if (isAddCustomPropertyOpen === 'true') {
      navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_ADD);
    }
  }, [navigateToScreen, isAddCustomPropertyOpen]);
};
